<template>
  <footer class="footer">
    <div
      class="container-fluid"
      :class="{ 'flex-end': !isFamilyDashboard && !isOperatorDashboard }"
    >
      <div
        v-if="isFamilyDashboard"
        class="need-help"
      >
        Need help?<br>
        Give us a call at: <a :href="gogoNumberURI">{{ gogoNumber }}</a><br>
        Email us at: <a href="mailto:support@gogograndparent.com">support@gogograndparent.com</a><br>
      </div>

      <div
        v-if="isOperatorDashboard"
        class="service-times text-left"
      >
        <div><strong>Gourmet Department</strong> | 855-754-5328 | 8AM ~ 12AM EST | 7 Days a week</div>

        <div><strong>Groceries Department</strong> | 855-222-4919 | 8AM ~ 12AM EST | 7 Days a week</div>

        <div><strong>Home Services</strong> | 855-464-6771 | 8AM ~ 8PM EST | Mondays-Fridays</div>

        <div><strong>Rides</strong> | 855-464-6872 | 24/7</div>

        <div><strong>Gogo Australia Rides</strong> | +61 134628 | 24/7</div>

        <div><strong>GoGo Care</strong> | 855-464-6603 | 9AM ~ 8PM EST | Mondays-Fridays</div>
      </div>

      <div class="project-info">
        <div>@ {{ new Date().getFullYear() }} Created with <i class="fa fa-heart heart" /> by GoGo Technologies Inc.</div>
        <div class="version">
          <small><small>&nbsp;({{ buildVersion }})&nbsp;</small></small>
        </div>
        <div
          v-if="!isUpToDate"
          class="out-of-date"
        >
          <i class="fa fa-exclamation-triangle" />
          <div>New version of the dashboard available!</div>
          <div>Please LOLI at your earliest convenience.</div>
        </div>
        <div
          v-if="isOperatorDashboard"
          class="footer-actions"
        >
          <button
            :disabled="isUploadingLogs"
            @click="uploadLogs"
          >
            SEND LOGS TO @ENG
          </button>
          <button
            :disabled="isPageRefreshing"
            @click="pageRefresh"
          >
            PAGE REFRESH
          </button>
        </div>
      </div>
    </div>
  </footer>
</template>

<script lang="ts">
import { onMounted, ref, computed } from 'vue';
import moment from 'moment-timezone';

import { useDashboard } from '@/use/app';
import { useCustomer } from '@/use/customer';
import { useIsUpToDate } from '@/use/version';
import { refreshPage } from '@/Utils/refreshPage';
import { log } from '@/Utils/logger';
import {
  showSuccessPopupDialog,
  showErrorPopupDialog,
  showWarningPopupDialog,
  showConfirmPopupDialog,
} from '@/use/swalDialog';

export default {
  setup: () => {
    const { isFamilyDashboard, isOperatorDashboard } = useDashboard();
    const { isAustralianUser } = useCustomer();

    const buildVersion = ref('');
    const { isUpToDate } = useIsUpToDate();
    const isUploadingLogs = ref(false);
    const isPageRefreshing = ref(false);

    const pageRefresh = async () => {
      if (isPageRefreshing.value) {
        return;
      }

      const confirmed = await showConfirmPopupDialog({
        title: 'Refresh dashboard Page?',
        text: 'This will disconnect any active call, clear cached app files and reload the page. Use this if the dashboard looks wrong after a deploy.',
        confirmButtonText: 'Page refresh',
        cancelButtonText: 'Cancel',
      });

      if (!confirmed) {
        return;
      }

      isPageRefreshing.value = true;

      log.info(
        {
          func: 'ContentFooter.pageRefresh',
        },
        'Page refresh requested',
      );

      try {
        await refreshPage();
      } catch (err) {
        log.error({ func: 'ContentFooter.pageRefresh', err }, 'Page refresh failed');
        await showErrorPopupDialog({
          title: 'Page refresh failed',
          text: 'Try Ctrl+Shift+R instead.',
        });
        isPageRefreshing.value = false;
      }
    };

    const uploadLogs = async () => {
      if (isUploadingLogs.value) {
        return;
      }
      isUploadingLogs.value = true;

      try {
        const result = await log.upload();
        if (!result.uploaded) {
          await showWarningPopupDialog({
            title: 'No logs to upload',
            text: 'There are no logs collected yet.',
            icon: 'info',
          });
          return;
        }
        await showSuccessPopupDialog({
          title: 'Logs uploaded',
          text: `Uploaded ${result.count} log entries.`,
        });
      } catch (err) {
        log.error({ func: 'ContentFooter.uploadLogs', err }, 'failed to upload operator logs');
        await showErrorPopupDialog({
          title: 'Failed to upload logs',
          text: 'Please try again. If the issue persists, contact engineering.',
        });
      } finally {
        isUploadingLogs.value = false;
      }
    };

    const gogoNumberURI = computed(() => {
      if (isFamilyDashboard.value && isAustralianUser.value) {
        return 'tel:+61134628';
      }

      return 'tel:+18554646872';
    });

    const gogoNumber = computed(() => {
      if (isFamilyDashboard.value && isAustralianUser.value) {
        return '+61 134628';
      }

      return '1 (855) 464-6872';
    });

    onMounted(() => {
      const buildTime = import.meta.env.BUILD_TIME;

      if (buildTime) {
        buildVersion.value = `${import.meta.env.VITE_BUILD_VERSION}, ${moment(buildTime).utc().format('YYYY-MM-DD HH:mm UTC')}`;
      }
    });

    return {
      buildVersion,
      isFamilyDashboard,
      isOperatorDashboard,
      gogoNumberURI,
      gogoNumber,
      isUpToDate,
      isUploadingLogs,
      isPageRefreshing,
      uploadLogs,
      pageRefresh,
    };
  },
};
</script>

<style lang="scss">
.footer {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  &.fixed-footer {
    position: fixed;
    right: 0;
    bottom: 0;
    width: 100%;
  }
  a {
    color: inherit;
    &:hover {
      text-decoration: none;
      color: inherit;
    }
  }
  .container-fluid {
    display: flex;
    justify-content: space-between;
    align-items: center;
    &.flex-end {
      justify-content: flex-end;
    }
  }
  .project-info {
    font-size: 14px !important;

    i.heart {
      color: #6d509c;
    }

    .out-of-date {
      padding: 4px;
      background-color: #ffa04f;
      i {
        float: left;
        font-size: 2em;
        margin-left: 7px;
        margin-top: 7px;
      }
    }

    .footer-actions {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-top: 4px;
    }
  }
  .need-help {
    text-align: left;
    a {
      font-weight: bold;

      &:hover {
        color: #3b73d8;
      }
    }
  }
}
</style>
